import React, { useState, useEffect } from 'react';
import { enforceMoneyFormat, enforceNumericFormat } from '../../utils/formUtils';
import { handleZipBlur } from '../../utils/zipAutoPopulate';
import US_STATES from '../../data/usStates';
import ZipCityPopup from '../shared/ZipCityPopup';
import { getAccessToken } from '../../services/authApi';

const PropertyInformation = ({ fieldValues, isRefiOrCashOut, onFieldChange, correspondentChannel }) => {
    const BOOL__MAP = {
        Yes: "Y",
        No: "N"
    };
    const [CEMA, setCEMA] = useState("");
    const [leasedStatus, setLeasedStatus] = useState("");
    const [token, setToken] = useState(null);
    const [zipPopup, setZipPopup] = useState({ results: [], cityFieldId: '', stateFieldId: '', countyFieldId: '' });
    const [selectedState, setSelectedState] = useState((fieldValues["14"] || "").toString().trim().toUpperCase());

    const [refiPurpose, setRefiPurpose] = useState("");
    const [yearAcquired, setYearAcquired] = useState("");
    const [originalCost, setOriginalCost] = useState("");
    const [existingLiens, setExistingLiens] = useState("");
    const [improvementsMade, setImprovementsMade] = useState("");

    useEffect(() => {
        const leasedVal = fieldValues["CX.BPL.LEASED"];
        if (leasedVal === "Y" || leasedVal === "N") {
            setLeasedStatus(leasedVal);
        }
        const cemaVal = fieldValues["CX.CEMA.NY.PROP"];
        if (cemaVal === "Y" || cemaVal === "N") {
            setCEMA(cemaVal);
        }

        document.getElementById("field-14").value = fieldValues["14"] || "";

        // sync selectedState from incoming values (ensure normalized)
        const fvState = (fieldValues["14"] || "").toString().trim().toUpperCase();
        setSelectedState(fvState);

    }, [fieldValues]);

    useEffect(() => {
        if (isRefiOrCashOut) {
            setRefiPurpose(fieldValues["299"] || "");
            setYearAcquired(fieldValues["24"] || "");
            setOriginalCost(fieldValues["25"] || "");
            setExistingLiens(fieldValues["26"] || "");
            setImprovementsMade(fieldValues["205"] || "");
        } else {
            setRefiPurpose("");
            setYearAcquired("");
            setOriginalCost("");
            setExistingLiens("");
            setImprovementsMade("");
        }
    }, [
        isRefiOrCashOut,
        fieldValues["299"],
        fieldValues["24"],
        fieldValues["25"],
        fieldValues["26"],
        fieldValues["205"]
    ]);

    const handleZipCodeBlur = async () => {
        let tokenToUse = token;
        if (!tokenToUse) {
            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            tokenToUse = await getAccessToken(authCode);
            setToken(tokenToUse);
        }

        const results = await handleZipBlur({
            zipFieldId: 'field-15',
            cityFieldId: 'field-12',
            stateFieldId: 'field-14',
            countyFieldId: 'field-13',
            token: tokenToUse,
            setPopupOptions: ({ results, cityFieldId, stateFieldId, countyFieldId }) =>
                setZipPopup({ results, cityFieldId, stateFieldId, countyFieldId })
        });

        // now handle single-result in the wrapper
        if (results && results.length === 1) {
            const abbrev = (results[0].abbrev || "").toString().trim().toUpperCase();
            setSelectedState(abbrev);
            const stateEl = document.getElementById('field-14');
            if (stateEl) stateEl.value = abbrev;
        }
    };


    const enableCEMA = Boolean(
        isRefiOrCashOut &&
        ((selectedState || (fieldValues["14"] || "")).toString().trim().toUpperCase() === "NY")
    );

    const showCEMA = Boolean(
        isRefiOrCashOut &&
        (selectedState || (fieldValues["14"] || "")).toString().trim().toUpperCase() === "NY"
    );

    useEffect(() => {
        if (!enableCEMA) {
            setCEMA("");
            const group = document.querySelectorAll('input[name="CEMA"]');
            group.forEach(cb => cb.checked = false);
        }
    }, [enableCEMA]);

    useEffect(() => {
        if (!showCEMA) {
            // clear React state
            setCEMA("");
            // clear DOM so legacy readers don't see a value
            const group = document.querySelectorAll('input[name="CEMA"]');
            group.forEach(cb => { cb.checked = false; });
        }
    }, [showCEMA]);

    return (
        <>
            <ZipCityPopup
                visible={zipPopup.results.length > 1}
                results={zipPopup.results}
                onSelect={(opt) => {
                    document.getElementById(zipPopup.cityFieldId).value = opt.city;
                    document.getElementById(zipPopup.stateFieldId).value = opt.abbrev;
                    document.getElementById(zipPopup.countyFieldId).value = opt.county;

                    setSelectedState((opt.abbrev || "").toString().trim().toUpperCase());

                    setZipPopup({ results: [], cityFieldId: '', stateFieldId: '', countyFieldId: '' });
                }}
                onClose={() => setZipPopup({ results: [], cityFieldId: '', stateFieldId: '', countyFieldId: '' })}
            />

            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
                <div className="section-header">2. Property Information and Purpose of Loan</div>

                {/* Address Fields */}
                <div className="px-4 pt-4">
                    <label className="form-label block mb-2">Subject Property Address</label>
                    <div className="grid grid-cols-4 gap-6 mb-4">
                        <div className="col-span-3">
                            <label className="form-label">Address</label>
                            <input
                                type="text"
                                className="form-input"
                                id="field-URLA.X73"
                                defaultValue={fieldValues["URLA.X73"] || ""}
                            />
                        </div>
                        <div className="col-span-1">
                            <label className="form-label">City</label>
                            <input
                                type="text"
                                className="form-input"
                                id="field-12"
                                defaultValue={fieldValues["12"] || ""}
                            />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6 mb-4">
                        <div>
                            <label className="form-label">State</label>
                            <select
                                id="field-14"
                                className="form-input w-full text-sm border rounded p-1"
                                defaultValue={fieldValues["14"] || ""}
                                onChange={e => {
                                    const v = e.target.value.toString().trim().toUpperCase();
                                    setSelectedState(v);

                                    const el = document.getElementById("field-14");
                                    if (el) el.value = v;
                                }}
                            >
                                <option value="">Select State</option>
                                {US_STATES.map(state => (
                                    <option key={state.abbrev} value={state.abbrev}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">ZIP</label>
                            <input
                                type="text"
                                className="form-input"
                                id="field-15"
                                defaultValue={fieldValues["15"] || ""}
                                onInput={enforceNumericFormat}
                                onBlur={handleZipCodeBlur}
                            />
                        </div>
                        <div>
                            <label className="form-label">County</label>
                            <input
                                type="text"
                                className="form-input"
                                id="field-13"
                                defaultValue={fieldValues["13"] || ""}
                            />
                        </div>
                        <div>
                            <label className="form-label">No. of Units</label>
                            <input
                                type="number"
                                className="form-input"
                                id="field-16"
                                defaultValue={fieldValues["16"] || ""}
                            />
                        </div>
                    </div>
                </div>

                {/* Legal Description & Year Built */}
                <div className="grid md:grid-cols-3 gap-6 px-4 pb-4">
                    <div className="md:col-span-2">
                        <label className="form-label">Legal Description of Subject Property (attach description if necessary)</label>
                        <textarea
                            rows="2"
                            className="form-textarea"
                            id="field-1846"
                            defaultValue={fieldValues["1846"] || ""}
                        />
                    </div>
                    <div>
                        <label className="form-label">Year Built</label>
                        <input
                            type="text"
                            className="form-input"
                            id="field-18"
                            defaultValue={fieldValues["18"] || ""}
                            onInput={enforceNumericFormat}
                        />
                    </div>
                </div>

                {/* Refinance Header */}
                <div className="section-note">Complete this line if this is a refinance loan.</div>

                {/* Refinance Section */}
                <div className="grid md:grid-cols-4 gap-6 px-4 pb-4">
                    <div>
                        <label className="form-label">Purpose of Refinance</label>
                        <select
                            id="field-299"
                            className={`form-input w-full text-sm border rounded p-1 ${!isRefiOrCashOut ? "cursor-not-allowed bg-gray-100" : ""}`}
                            value={refiPurpose}
                            disabled={!isRefiOrCashOut}
                            onChange={(e) => {
                                const value = e.target.value;
                                setRefiPurpose(value);
                                onFieldChange && onFieldChange("299", value);
                            }}
                        >
                            <option value="">Select an Option</option>
                            <option value="CashOutLimited">Cash-Out Limited</option>
                            <option value="CashOutOther">Cash-Out Other</option>
                            <option value="ChangeInRateTerm">No Cash-Out Rate/Term</option>
                        </select>
                    </div>
                    <div>
                        <label className="form-label">Year Acquired</label>
                        <input
                            type="text"
                            className={`form-input ${!isRefiOrCashOut ? "cursor-not-allowed bg-gray-100" : ""}`}
                            id="field-24"
                            value={yearAcquired}
                            readOnly={!isRefiOrCashOut}
                            onInput={(e) => {
                                if (!isRefiOrCashOut) return;
                                enforceNumericFormat(e);
                                setYearAcquired(e.target.value);
                            }}
                        />
                    </div>
                    <div>
                        <label className="form-label">Original Cost</label>
                        <div className="input-with-symbol">
                            <span className="mr-1">$</span>
                            <input
                                type="text"
                                className={`form-input ${!isRefiOrCashOut ? "cursor-not-allowed bg-gray-100" : ""}`}
                                id="field-25"
                                value={originalCost}
                                readOnly={!isRefiOrCashOut}
                                onChange={(e) => {
                                    if (!isRefiOrCashOut) return;
                                    setOriginalCost(e.target.value);
                                }}
                                onBlur={(e) => {
                                    if (!isRefiOrCashOut) return;
                                    enforceMoneyFormat(e);
                                    setOriginalCost(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="form-label">Amount Existing Liens</label>
                        <div className="input-with-symbol">
                            <span className="mr-1">$</span>
                            <input
                                type="text"
                                className={`form-input ${!isRefiOrCashOut ? "cursor-not-allowed bg-gray-100" : ""}`}
                                id="field-26"
                                value={existingLiens}
                                readOnly={!isRefiOrCashOut}
                                onChange={(e) => {
                                    if (!isRefiOrCashOut) return;
                                    setExistingLiens(e.target.value);
                                }}
                                onBlur={(e) => {
                                    if (!isRefiOrCashOut) return;
                                    enforceMoneyFormat(e);
                                    setExistingLiens(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="form-label">Improvements Made</label>
                        <input
                            type="text"
                            className={`form-input ${!isRefiOrCashOut ? "cursor-not-allowed bg-gray-100" : ""}`}
                            id="field-205"
                            value={improvementsMade}
                            readOnly={!isRefiOrCashOut}
                            onChange={(e) => {
                                if (!isRefiOrCashOut) return;
                                setImprovementsMade(e.target.value);
                            }}
                        />
                    </div>
                </div>

                {showCEMA && (
                    <div className="px-4 pb-2">
                        <label className="form-label">Is this Refinance being completed as a New York CEMA (Consolidation, Extension, and Modification Agreement)?<span class="text-red-600 ml-1">*</span></label>

                        <div className="flex gap-6 mb-4">
                            {Object.entries(BOOL__MAP).map(([uiLabel, encompassValue]) => (
                                <label className="checkbox-label" key={uiLabel}>
                                    <input
                                        type="checkbox"
                                        className="checkbox-accent"
                                        id={`field-CX.CEMA.NY.PROP`}
                                        name="CEMA"
                                        value={encompassValue}
                                        disabled={!enableCEMA}
                                        checked={CEMA === encompassValue}
                                        onChange={() => {
                                            const newValue = CEMA === encompassValue ? "" : encompassValue;
                                            setCEMA(newValue);

                                            const group = document.querySelectorAll('input[name="CEMA"]');
                                            group.forEach(cb => cb.checked = (cb.value === newValue));
                                        }}
                                    />
                                    {uiLabel}
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Entity & Business Owners */}
                <div className="grid md:grid-cols-2 gap-6 px-4 pb-4">
                    <div>
                        <label className="form-label">
                            Title will be held in what Name(s) or Entity
                            <span className="form-label-note">(Must Complete)</span>
                        </label>
                        <input
                            className="form-input"
                            type="text"
                            id="field-URLA.X137"
                            defaultValue={fieldValues["URLA.X137"] || ""}
                        />
                    </div>
                    <div>
                        <label className="form-label">How Many Business Owners in Entity (Max 4)</label>
                        <input
                            type="number"
                            className="form-input"
                            id="field-CX.BPL.NO.OWNERS"
                            defaultValue={fieldValues["CX.BPL.NO.OWNERS"] || ""}
                        />
                    </div>
                </div>

                {/* Member Info - 4 members */}
                {[1, 3].map((startIndex) => (
                    <div key={startIndex} className="grid md:grid-cols-4 gap-6 px-4 pb-2">
                        {[startIndex, startIndex + 1].map((i) => (
                            <React.Fragment key={i}>
                                <div>
                                    <label className="form-label">Name of Member {i}</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        id={`field-CX.BPL.MEMBER.NAME.${i}`}
                                        defaultValue={fieldValues[`CX.BPL.MEMBER.NAME.${i}`] || ""}
                                    />
                                </div>
                                <div>
                                    <label className="form-label">% of Ownership</label>
                                    <input
                                        className="form-input"
                                        type="text"
                                        id={`field-CX.BPL.MEMBER.OWNPERC.${i}`}
                                        defaultValue={fieldValues[`CX.BPL.MEMBER.OWNPERC.${i}`] || ""}
                                        onInput={enforceNumericFormat}
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                ))}


                {/* Lease Info */}
                <div className="px-4 pb-6">
                    {!correspondentChannel && (
                        <div className='mb-4'>
                            <label className="form-label">What is the Gross Rent?</label>
                            <div className="input-with-symbol">
                                <span className="mr-1">$</span>
                                <input
                                    type="text"
                                    className="form-input-medium"
                                    id="field-CX.GROSS.RENT"
                                    defaultValue={fieldValues["CX.GROSS.RENT"] || ""}
                                    onBlur={enforceMoneyFormat}
                                />
                            </div>
                        </div>
                    )}

                    <label className="form-label">Is the subject property currently leased?</label>
                    <div className="flex gap-6 mb-4">
                        {Object.entries(BOOL__MAP).map(([uiLabel, encompassValue]) => (
                            <label className="checkbox-label" key={uiLabel}>
                                <input
                                    type="checkbox"
                                    className="checkbox-accent"
                                    id={`field-CX.BPL.LEASED`}
                                    name="leased"
                                    value={encompassValue}
                                    checked={leasedStatus === encompassValue}
                                    onChange={() => {
                                        const newValue = leasedStatus === encompassValue ? "" : encompassValue;
                                        setLeasedStatus(newValue);

                                        const group = document.querySelectorAll('input[name="leased"]');
                                        group.forEach(cb => cb.checked = (cb.value === newValue));
                                    }}
                                />
                                {uiLabel}
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default PropertyInformation;
